import React, { useState, useEffect } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Download, Upload, AlertTriangle, CheckCircle2, XCircle, Clock, Shield, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import apiClient from '@/services/api';

const SystemBackup = () => {
  const [backupLoading, setBackupLoading] = useState(false);
  const [restoreFile, setRestoreFile] = useState(null);
  const [showRestoreDialog, setShowRestoreDialog] = useState(false);
  const [confirmText, setConfirmText] = useState('');
  const [restoreLoading, setRestoreLoading] = useState(false);
  const [logs, setLogs] = useState([]);
  const [logsLoading, setLogsLoading] = useState(true);

  useEffect(() => {
    loadLogs();
  }, []);

  const loadLogs = async () => {
    try {
      const res = await apiClient.get('/admin/backup/logs');
      setLogs(res.data.logs || []);
    } catch {
      // Ignore — may not have logs yet
    } finally {
      setLogsLoading(false);
    }
  };

  const handleBackup = async () => {
    setBackupLoading(true);
    try {
      const response = await apiClient.get('/admin/backup', { responseType: 'blob' });
      const disposition = response.headers['content-disposition'] || '';
      const match = disposition.match(/filename=(.+)/);
      const filename = match ? match[1] : `backup_${new Date().toISOString().slice(0,16).replace(/[-:T]/g,'_')}.gz`;

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toast.success('Backup downloaded successfully');
      loadLogs();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Backup failed');
    } finally {
      setBackupLoading(false);
    }
  };

  const handleRestoreClick = () => {
    if (!restoreFile) {
      toast.error('Select a backup file first');
      return;
    }
    setShowRestoreDialog(true);
    setConfirmText('');
  };

  const handleRestore = async () => {
    if (confirmText !== 'CONFIRM') {
      toast.error('Type CONFIRM to proceed');
      return;
    }
    setRestoreLoading(true);
    try {
      const formData = new FormData();
      formData.append('file', restoreFile);
      formData.append('confirmation', 'CONFIRM');

      await apiClient.post('/admin/restore', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 300000,
      });

      toast.success('Database restored successfully');
      setShowRestoreDialog(false);
      setRestoreFile(null);
      setConfirmText('');
      loadLogs();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Restore failed');
    } finally {
      setRestoreLoading(false);
    }
  };

  const formatTimestamp = (ts) => {
    if (!ts) return '';
    return new Date(ts).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div className="space-y-6" data-testid="system-backup-section">
      {/* Header */}
      <div className="flex items-center gap-3 mb-2">
        <Shield className="w-6 h-6 text-purple-600" />
        <div>
          <h2 className="text-xl font-bold text-gray-900">System Backup & Restore</h2>
          <p className="text-sm text-gray-500">Full MongoDB backup for disaster recovery. SUPER ADMIN only.</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Backup Card */}
        <Card className="border-2 border-green-100" data-testid="backup-card">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-green-700">
              <Download className="w-5 h-5" />
              Download Backup
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-gray-600">
              Creates a compressed archive of the entire <strong>sspice_pos</strong> database using <code>mongodump</code>.
              The file will download as <code>.gz</code>.
            </p>
            <div className="bg-green-50 border border-green-200 rounded-lg p-3">
              <p className="text-xs text-green-700 font-medium">Includes: All collections (orders, stores, users, menu items, customers, etc.)</p>
            </div>
            <Button
              data-testid="download-backup-btn"
              onClick={handleBackup}
              disabled={backupLoading}
              className="w-full bg-green-600 hover:bg-green-700 text-white"
            >
              {backupLoading ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Creating Backup...</>
              ) : (
                <><Download className="w-4 h-4 mr-2" /> Download Full Database Backup</>
              )}
            </Button>
          </CardContent>
        </Card>

        {/* Restore Card */}
        <Card className="border-2 border-red-100" data-testid="restore-card">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-red-700">
              <Upload className="w-5 h-5" />
              Upload & Restore
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="bg-red-50 border border-red-200 rounded-lg p-3">
              <div className="flex items-start gap-2">
                <AlertTriangle className="w-4 h-4 text-red-600 mt-0.5 flex-shrink-0" />
                <p className="text-xs text-red-700 font-medium">
                  WARNING: Restoring will overwrite the ENTIRE database with the backup contents. This action cannot be undone.
                </p>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Select backup file (.gz)</label>
              <Input
                data-testid="restore-file-input"
                type="file"
                accept=".gz"
                onChange={(e) => setRestoreFile(e.target.files[0] || null)}
                className="cursor-pointer"
              />
              {restoreFile && (
                <p className="text-xs text-gray-500 mt-1">
                  Selected: <strong>{restoreFile.name}</strong> ({(restoreFile.size / 1024 / 1024).toFixed(2)} MB)
                </p>
              )}
            </div>
            <Button
              data-testid="restore-btn"
              onClick={handleRestoreClick}
              disabled={!restoreFile}
              variant={restoreFile ? "destructive" : "outline"}
              className={`w-full ${!restoreFile ? 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed' : ''}`}
            >
              <Upload className="w-4 h-4 mr-2" />
              {restoreFile ? 'Upload & Restore Backup' : 'Select a .gz file above to restore'}
            </Button>
          </CardContent>
        </Card>
      </div>

      {/* Activity Log */}
      <Card data-testid="backup-logs-card">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-gray-700 text-base">
            <Clock className="w-5 h-5" />
            Backup & Restore Activity Log
          </CardTitle>
        </CardHeader>
        <CardContent>
          {logsLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
            </div>
          ) : logs.length === 0 ? (
            <p className="text-sm text-gray-400 text-center py-6">No activity yet</p>
          ) : (
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {logs.map((log, i) => (
                <div key={i} className="flex items-center gap-3 py-2 px-3 bg-gray-50 rounded-lg text-sm" data-testid={`log-entry-${i}`}>
                  {log.success ? (
                    <CheckCircle2 className="w-4 h-4 text-green-600 flex-shrink-0" />
                  ) : (
                    <XCircle className="w-4 h-4 text-red-600 flex-shrink-0" />
                  )}
                  <span className={`font-semibold text-xs px-1.5 py-0.5 rounded ${log.action === 'BACKUP' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                    {log.action}
                  </span>
                  <span className="text-gray-600 flex-1 truncate">{log.details || '-'}</span>
                  <span className="text-xs text-gray-400 whitespace-nowrap">{log.user_name}</span>
                  <span className="text-xs text-gray-400 whitespace-nowrap">{formatTimestamp(log.timestamp)}</span>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Restore Confirmation Dialog */}
      <Dialog open={showRestoreDialog} onOpenChange={setShowRestoreDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-700">
              <AlertTriangle className="w-5 h-5" />
              Confirm Database Restore
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <p className="text-sm text-red-800 font-medium mb-2">This will overwrite the entire database.</p>
              <ul className="text-xs text-red-700 space-y-1 list-disc pl-4">
                <li>All current data will be replaced</li>
                <li>All active sessions may be disrupted</li>
                <li>This action cannot be undone</li>
              </ul>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Type <strong className="text-red-600">CONFIRM</strong> to proceed
              </label>
              <Input
                data-testid="restore-confirm-input"
                value={confirmText}
                onChange={(e) => setConfirmText(e.target.value)}
                placeholder="Type CONFIRM"
                className="border-red-300 focus:ring-red-500"
              />
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setShowRestoreDialog(false)} disabled={restoreLoading}>
              Cancel
            </Button>
            <Button
              data-testid="restore-confirm-btn"
              variant="destructive"
              onClick={handleRestore}
              disabled={confirmText !== 'CONFIRM' || restoreLoading}
            >
              {restoreLoading ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Restoring...</>
              ) : (
                'Restore Database'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SystemBackup;
